/**
 * @file AssignLicenseToUserModal.tsx
 * @desc Modal to assign available licenses to one or more users that have no license yet
 * @copyright All rights reserved @ Inarix
 */

import { FC, useState, useMemo, useRef, useCallback } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  Typography,
  Paper,
  Tooltip,
  LinearProgress,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { LoadingButton } from "@mui/lab";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { TFunction } from "i18next";

import { selectUnassignedLicenses } from "../../redux/slices/licenses/licenseList";
import {
  bindLicensesToUsers,
  AssignResult,
} from "../../redux/actions/licenses/licenseListAsyncActions";
import { LicenseAssignationModalStyles } from "./licenseStyles/LicenseAssignationModal";
import { AssignLicenseToUserModalStyles as S } from "./licenseStyles/AssignLicenseToUserModal.styles";

export interface UserToAssign {
  userId: string;
  userGroupId: number;
  username: string;
}

type AssignStatus = "idle" | "loading" | "success" | "error";

interface PairState {
  status: AssignStatus;
  progress: number;
}

interface AssignLicenseToUserModalProps {
  users: UserToAssign[];
  initData: (dispatch: ThunkDispatch<any, any, any>) => void;
  t: TFunction<"global", undefined>;
  onClose: () => void;
}

const PROGRESS_TICK_MS = 80;
const PROGRESS_MAX_BEFORE_DONE = 85;

const AssignLicenseToUserModal: FC<AssignLicenseToUserModalProps> = ({
  users: initialUsers,
  initData,
  t,
  onClose,
}) => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const availableLicenses = useSelector(selectUnassignedLicenses);

  const [activeUsers, setActiveUsers] = useState<UserToAssign[]>(initialUsers);
  const [pairStates, setPairStates] = useState<Record<string, PairState>>({});
  const [committedPairs, setCommittedPairs] = useState<
    Array<{ user: UserToAssign; license: (typeof availableLicenses)[0] | null }>
  >([]);
  const intervalsRef = useRef<Record<string, ReturnType<typeof setInterval>>>(
    {},
  );

  const pairs = useMemo(
    () =>
      activeUsers.map((user, i) => ({
        user,
        license: availableLicenses[i] ?? null,
      })),
    [activeUsers, availableLicenses],
  );

  // After assignment starts, use the snapshot so the UI doesn't break when
  // the Redux store updates and availableLicenses drops to 0.
  const displayPairs = committedPairs.length > 0 ? committedPairs : pairs;

  const assignablePairs = displayPairs.filter((p) => p.license !== null);

  const isRunning = Object.values(pairStates).some(
    (s) => s.status === "loading",
  );
  const allDone =
    assignablePairs.length > 0 &&
    assignablePairs.every((p) => {
      const s = pairStates[p.user.userId];
      return s?.status === "success" || s?.status === "error";
    });

  const allSuccessful =
    allDone &&
    assignablePairs.every(
      (p) => pairStates[p.user.userId]?.status === "success",
    );

  const startProgressTick = useCallback((userId: string) => {
    const id = setInterval(() => {
      setPairStates((prev) => {
        const cur = prev[userId];
        if (!cur || cur.status !== "loading") {
          clearInterval(id);
          return prev;
        }
        const next = Math.min(
          cur.progress + (Math.random() * 6 + 3),
          PROGRESS_MAX_BEFORE_DONE,
        );
        return { ...prev, [userId]: { ...cur, progress: next } };
      });
    }, PROGRESS_TICK_MS);
    intervalsRef.current[userId] = id;
  }, []);

  const finishPair = useCallback(
    (userId: string, status: "success" | "error") => {
      clearInterval(intervalsRef.current[userId]);
      delete intervalsRef.current[userId];
      setPairStates((prev) => ({
        ...prev,
        [userId]: {
          status,
          progress: status === "success" ? 100 : (prev[userId]?.progress ?? 0),
        },
      }));
    },
    [],
  );

  const handleConfirm = async () => {
    if (assignablePairs.length === 0) return;

    setCommittedPairs(pairs);

    const initial: Record<string, PairState> = {};
    assignablePairs.forEach((p) => {
      initial[p.user.userId] = { status: "loading", progress: 0 };
    });
    setPairStates(initial);
    assignablePairs.forEach((p) => startProgressTick(p.user.userId));

    const results: AssignResult[] = await (
      dispatch(
        bindLicensesToUsers(
          assignablePairs.map((p) => ({
            userId: p.user.userId,
            userGroupId: p.user.userGroupId,
            licenseId: p.license!.id,
          })),
        ),
      ) as any
    ).unwrap();

    results.forEach((r) => finishPair(r.userId, r.status));

    const hadSuccess = results.some((r) => r.status === "success");
    if (hadSuccess) {
      initData(dispatch);
    }
  };

  const removeUser = (userId: string) => {
    setActiveUsers((prev) => prev.filter((u) => u.userId !== userId));
  };

  return (
    <Box sx={S.container}>
      <Typography variant="h5" component="h2" gutterBottom>
        {t("licenses.assign_license_to_user")}
      </Typography>

      {/* Users chips */}
      <Typography
        variant="body2"
        color="text.secondary"
        sx={S.assigningToLabel}
      >
        {t("licenses.assigning_to")}:
      </Typography>
      <Box sx={S.chipsContainer}>
        {activeUsers.map((u) => {
          const s = pairStates[u.userId];
          const isDone = s?.status === "success" || s?.status === "error";
          return (
            <Chip
              key={u.userId}
              label={u.username}
              onDelete={
                !isRunning && !isDone && activeUsers.length > 1
                  ? () => removeUser(u.userId)
                  : undefined
              }
              color={
                s?.status === "success"
                  ? "success"
                  : s?.status === "error"
                    ? "warning"
                    : "primary"
              }
              variant="outlined"
              size="small"
            />
          );
        })}
        {activeUsers.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {t("licenses.no_users_selected")}
          </Typography>
        )}
      </Box>

      {/* Assignment pairs */}
      {activeUsers.length > 0 && (
        <Paper variant="outlined" sx={S.pairsPaper}>
          <Box sx={S.pairsScrollBox}>
            {displayPairs.map((pair, index) => {
              const isLast = index === displayPairs.length - 1;
              const expiry = pair.license
                ? new Date(pair.license.expiryDate)
                : null;
              const isExpiringSoon =
                expiry &&
                expiry.getTime() - Date.now() < 30 * 24 * 60 * 60 * 1000;
              const state = pairStates[pair.user.userId];

              return (
                <Box key={pair.user.userId}>
                  <Box sx={S.pairRowBox(state?.status !== "idle" && !!state)}>
                    {pair.license ? (
                      <>
                        {/* Main row: license → user + status icon */}
                        <Box sx={S.mainRow}>
                          {/* License info */}
                          <Box sx={S.licenseInfoBox}>
                            <Typography
                              variant="body2"
                              fontFamily="monospace"
                              fontWeight={500}
                              noWrap
                              title={pair.license.id}
                            >
                              {t("licenses.licence_id")}:
                              <span style={S.licenceIdSuffix}>
                                {pair.license.id.slice(0, 14)}…
                              </span>
                            </Typography>
                            <Stack
                              direction="row"
                              alignItems="center"
                              gap={0.5}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {t("licenses.expiration")}:{" "}
                                {expiry!.toLocaleDateString()}
                              </Typography>
                              {isExpiringSoon && (
                                <Tooltip title={t("licenses.expSoon")}>
                                  <WarningAmberIcon sx={S.expiringSoonIcon} />
                                </Tooltip>
                              )}
                            </Stack>
                          </Box>

                          {/* Arrow — green on success */}
                          <ArrowForwardIcon
                            sx={S.arrowIcon(state?.status === "success")}
                            fontSize="small"
                          />

                          {/* User + status icon */}
                          <Box sx={S.userStatusBox}>
                            <Typography variant="body2" fontWeight={500} noWrap>
                              {pair.user.username}
                            </Typography>
                            {state?.status === "success" && (
                              <CheckCircleIcon sx={S.successIcon} />
                            )}
                            {state?.status === "error" && (
                              <Tooltip title={t("licenses.assign_error")}>
                                <CancelIcon sx={S.errorIcon} />
                              </Tooltip>
                            )}
                          </Box>
                        </Box>

                        {/* Progress bar */}
                        {(state?.status === "loading" ||
                          state?.status === "success" ||
                          state?.status === "error") && (
                          <Box sx={S.progressBarBox}>
                            <LinearProgress
                              variant="determinate"
                              value={state.progress}
                              sx={S.progressBar(state.status, PROGRESS_TICK_MS)}
                            />
                          </Box>
                        )}
                      </>
                    ) : (
                      <Box sx={S.noLicenseBox}>
                        <WarningAmberIcon sx={S.noLicenseIcon} />
                        <Typography variant="body2" color="warning.dark">
                          {t("licenses.no_license_available_for")}{" "}
                          <strong>{pair.user.username}</strong>
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  {!isLast && <Divider />}
                </Box>
              );
            })}
          </Box>
        </Paper>
      )}

      {allDone && (
        <Alert severity={allSuccessful ? "success" : "warning"} sx={{ mb: 2 }}>
          {allSuccessful
            ? t("licenses.all_assigned_success")
            : t("licenses.partial_assigned")}
        </Alert>
      )}

      <Stack direction="row" spacing={2}>
        <LoadingButton
          loading={isRunning}
          variant="contained"
          color="primary"
          fullWidth
          disabled={assignablePairs.length === 0 || allDone}
          sx={LicenseAssignationModalStyles.button}
          onClick={handleConfirm}
        >
          {t("Licenses.assign_button")}
          {assignablePairs.length > 1 ? ` (${assignablePairs.length})` : ""}
        </LoadingButton>
        <Button
          disabled={isRunning}
          variant="outlined"
          color="inherit"
          fullWidth
          sx={LicenseAssignationModalStyles.button}
          onClick={onClose}
        >
          {allDone ? t("Common_options.Close") : t("Common_options.Cancel")}
        </Button>
      </Stack>
    </Box>
  );
};

export default AssignLicenseToUserModal;
